import { type BeforeMount, Editor } from "@monaco-editor/react";
import { Box, useTheme } from "@mui/material";
import classNames from "classnames";
import { editor, KeyCode, KeyMod } from "monaco-editor";
import type * as Monaco from "monaco-editor";
import * as React from "react";
import { useCallback } from "react";

import { QueryTarget, type RoboqlExternalCtx } from "@/shared/domain/query";
import { useIsDarkMode } from "@/shared/hooks/useIsDarkMode";

import {
  singletonCompletionProvider,
  roboqlEditorBeforeMount,
  refreshRoboqlTheme,
  ROBOQL_MONACO_THEME,
  ROBOQL_QUICK_SUGGESTIONS,
} from "./monaco";
import styles from "./RoboqlEditor.module.css";

/**
 * Hosts Monaco's overflowing widgets (the autocomplete list, hovers) on
 * `<body>` instead of inside the editor, for the lifetime of one editor.
 *
 * Monaco otherwise renders those widgets in place, which puts them at the
 * mercy of whatever the editor is embedded in: a popover clips them, and —
 * because Base UI positions a popover with a `transform`, and a transformed
 * ancestor is the containing block for `position: fixed` descendants — even
 * an unclipped popover would place them wrong. Nothing transforms `<body>`,
 * so hosting them there restores the meaning of the viewport coordinates
 * `fixedOverflowWidgets` makes Monaco write. The two are a pair: neither
 * option is correct without the other.
 *
 * The host has to carry Monaco's own classes. `monaco-editor` is where the
 * theme's colour variables are declared (`.monaco-editor, .monaco-diff-editor,
 * .monaco-component { --vscode-…: … }`) and what the widget CSS is scoped to;
 * without it the list renders as unstyled rows. `vs` is the theme-type
 * selector Monaco stamps on the editor root, and the RoboQL theme pins its
 * base to `vs` in both light and dark (see `monaco/theme.ts`).
 */
function useOverflowWidgetsHost(): HTMLDivElement | undefined {
  const hostRef = React.useRef<HTMLDivElement>();
  if (hostRef.current === undefined && typeof document !== "undefined") {
    const host = document.createElement("div");
    host.className = classNames("monaco-editor", "vs", styles.overflowWidgets);
    hostRef.current = host;
  }

  React.useEffect(() => {
    const host = hostRef.current;
    if (host === undefined) {
      return;
    }
    document.body.appendChild(host);
    return () => host.remove();
  }, []);

  return hostRef.current;
}

export interface RoboqlEditorProps {
  containerBorder?: number;
  containerClassName?: classNames.Argument;
  editorClassName?: classNames.Argument;
  editorOptions?: editor.IStandaloneEditorConstructionOptions;
  initialValue?: string;
  triggerCompletionOnEmptyInitialValue?: boolean;
  onChange?: (value: string | undefined) => void;
  /** Invoked on ⌘/Ctrl+Enter. The consumer owns the guards — this fires
   *  whenever the chord is pressed inside the editor. */
  onCommit?: () => void;
  /** Take focus on mount and land the caret after a non-empty
   *  `initialValue`. Off by default: editors embedded in a page (the search
   *  bar, the trigger form) must not grab focus when the page renders. */
  focusEndOnMount?: boolean;
  target?: QueryTarget;
  roboqlExternalCtx: RoboqlExternalCtx;
}

export const RoboqlEditor: React.FC<RoboqlEditorProps> = ({
  containerBorder = 0,
  containerClassName,
  editorClassName,
  editorOptions,
  initialValue = "",
  onChange = () => {},
  onCommit,
  focusEndOnMount = false,
  target = QueryTarget.Datasets,
  triggerCompletionOnEmptyInitialValue = true,
  roboqlExternalCtx,
}) => {
  const theme = useTheme();
  const overflowWidgetsHost = useOverflowWidgetsHost();

  // The Monaco theme is hex-only, so it is baked from the palette the document
  // resolves right now; re-bake it whenever the app's mode flips.
  //
  // The instance comes from `<Editor>`'s `beforeMount` rather than from
  // `useMonaco()`. That hook starts its own `loader.init()` with no rejection
  // handler and cancels it on unmount, and the loader answers a cancelled load
  // by rejecting with a plain object once Monaco actually arrives. An editor
  // that closes before Monaco finishes loading from the CDN therefore leaks an
  // unhandled rejection seconds later, which fails the whole component test
  // run in CI. `<Editor>` handles that cancellation itself.
  const [monaco, setMonaco] = React.useState<typeof Monaco>();
  const isDarkMode = useIsDarkMode();
  React.useEffect(() => {
    if (monaco) {
      refreshRoboqlTheme(monaco);
    }
  }, [monaco, isDarkMode]);
  const beforeMount: BeforeMount = (instance: typeof Monaco) => {
    roboqlEditorBeforeMount(instance);
    setMonaco(instance);
  };

  React.useEffect(() => {
    singletonCompletionProvider().externalCtx = roboqlExternalCtx;
  }, [roboqlExternalCtx]);

  const editorRef = React.useRef<editor.IStandaloneCodeEditor>();
  const maybeTriggerCompletion = useCallback(() => {
    if (
      editorRef.current &&
      initialValue.trim() === "" &&
      triggerCompletionOnEmptyInitialValue
    ) {
      editorRef.current.trigger("", "editor.action.triggerSuggest", null);
    }
  }, [editorRef, initialValue, triggerCompletionOnEmptyInitialValue]);

  const innerOnChange = (
    value: string | undefined,
    event: editor.IModelContentChangedEvent,
  ) => {
    onChange(value);

    // If the last completion added a '[', we want to trigger autocomplete. This makes it so completing
    // msgpaths[] will drop a user in the middle of the '[]' and also provide any existing message paths as
    // suggestions. We need to add a small amount of delay in order for this to work smoothly.
    const triggerForMessagePath = event.changes.some(
      (change: editor.IModelContentChange) => {
        return (
          change.text.includes("msgpaths[") || change.text.includes("fields[")
        );
      },
    );

    if (editorRef.current && triggerForMessagePath) {
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.trigger("", "editor.action.triggerSuggest", null);
        }
      }, 50);
    }
  };

  // The overflowing widgets carry viewport coordinates, and Monaco only
  // recomputes those when the editor renders. Scrolling the page under the
  // editor moves the editor without touching it — a popover answers by
  // repositioning itself, the widget stays where it was — so dismiss the
  // suggestion list rather than leave it stranded away from its caret.
  // Monaco's own scrolling is exempt: that goes through a render, which
  // repositions the widget correctly.
  React.useEffect(() => {
    const dismissOnOutsideScroll = (event: Event) => {
      const mountedEditor = editorRef.current;
      const editorNode = mountedEditor?.getDomNode();
      if (
        !mountedEditor ||
        !editorNode ||
        (event.target instanceof Node && editorNode.contains(event.target))
      ) {
        return;
      }
      mountedEditor.trigger("editor", "hideSuggestWidget", []);
    };

    // Capture, so scrolling containers between the editor and the window are
    // caught too — scroll events do not bubble.
    window.addEventListener("scroll", dismissOnOutsideScroll, true);
    return () =>
      window.removeEventListener("scroll", dismissOnOutsideScroll, true);
  }, []);

  // `addCommand` binds once on mount, but `onCommit` closes over per-render
  // state (query text, mutation status). Reading through a ref keeps the
  // keybinding on the latest handler instead of the mount-time one.
  const onCommitRef = React.useRef(onCommit);
  React.useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const onMount = (mountedEditor: editor.IStandaloneCodeEditor) => {
    editorRef.current = mountedEditor;

    mountedEditor.addCommand(KeyMod.CtrlCmd | KeyCode.Enter, () =>
      onCommitRef.current?.(),
    );

    // Seeded from an existing query: the user is amending it, so land the
    // caret after what's already there. A fresh editor keeps its default
    // position and its empty-value suggestion trigger.
    const model = mountedEditor.getModel();
    if (focusEndOnMount && initialValue !== "" && model) {
      mountedEditor.setPosition(model.getFullModelRange().getEndPosition());
      mountedEditor.focus();
    }

    maybeTriggerCompletion();
  };

  // New completion data may have arrived, so refresh the suggestion list —
  // but only for someone who is actually in the editor. Ungated, a query
  // resolving after the editor has been left (Execute closing the menu, say)
  // pops an unasked-for list, which usually has nothing to offer.
  React.useEffect(() => {
    if (editorRef.current?.hasTextFocus()) {
      editorRef.current.trigger("editor", "hideSuggestWidget", []);
      editorRef.current.trigger("", "editor.action.triggerSuggest", null);
    }

    maybeTriggerCompletion();
  }, [
    maybeTriggerCompletion,
    target,
    roboqlExternalCtx.datasets?.metadataKeys?.isSuccess,
    roboqlExternalCtx.datasets?.tags?.isSuccess,
    roboqlExternalCtx.files?.metadataKeys?.isSuccess,
    roboqlExternalCtx.files?.tags?.isSuccess,
    roboqlExternalCtx.topics?.names?.isSuccess,
    roboqlExternalCtx.topics?.messagePaths?.isSuccess,
    roboqlExternalCtx.customFields?.isSuccess,
    roboqlExternalCtx.customFieldsEnabled,
  ]);

  return (
    <Box
      border={containerBorder}
      borderColor={theme.border.color}
      className={classNames(styles.container, containerClassName)}
      data-cy={"roboqlEditor"}
    >
      <Editor
        beforeMount={beforeMount}
        onMount={onMount}
        height={"100%"}
        defaultLanguage={"roboql"}
        theme={ROBOQL_MONACO_THEME}
        className={classNames(editorClassName)}
        options={{
          // Pair: the widgets live on `<body>` and Monaco positions them
          // against the viewport. See `useOverflowWidgetsHost`.
          overflowWidgetsDomNode: overflowWidgetsHost,
          fixedOverflowWidgets: true,

          // Another option is "interval", which only shows your current line.
          lineNumbers: "on",
          scrollBeyondLastLine: false,
          readOnly: false,

          // If unset, this defaults to 5, and pads line numbers, so they'll look uniform up to 5 digits of line number.
          // That uses a ton of horizontal space for basically a huge margin, so we want to disable it.
          lineNumbersMinChars: 2,

          // This stops Monaco from intercepting the right-click menu with its own command menu, and defaults to
          // whatever the browser was normally doing.
          contextmenu: false,

          // This gets rid of the thing that shows a really tiny version of your whole codebase
          minimap: {
            enabled: false,
          },

          // This gets rid of persistent scrollbars
          scrollbar: {
            horizontal: "hidden",
            vertical: "hidden",
            useShadows: false,
          },

          // This gets rid of an outline of where the scroll bar used to be
          overviewRulerBorder: false,

          // This gets rid of a little grey line above the outline
          overviewRulerLanes: 0,

          fontSize: theme.typography.fontSize,

          renderLineHighlight: "none",

          wordWrap: "on",

          // RoboQL values are quoted, and Monaco's defaults keep the
          // suggestion widget closed inside strings.
          quickSuggestions: ROBOQL_QUICK_SUGGESTIONS,

          ...editorOptions,
        }}
        value={initialValue}
        onChange={innerOnChange}
      />
    </Box>
  );
};
